import { differenceInCalendarDays } from 'date-fns';
import { ScoringInvitee, ScoringRequestRow, ScoringRequestStatus } from '../../../../types/advise';
import { isValidEmail } from '../../../../utils/email';
import { formatGeneratedAt } from '../../../../utils/summaryDate';

export interface InviteeDraft {
  // Stable across edits and removals, for React's key.
  rowId: number;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  scope: string[];
  // Why the last send didn't email this invitee.
  error?: string;
}

let nextRowId = 0;

export const emptyInvitee = (scope: string[] = []): InviteeDraft => ({
  rowId: nextRowId++,
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  scope
});

export const isBlankInvitee = (invitee: InviteeDraft): boolean =>
  !invitee.firstName.trim() && !invitee.lastName.trim() && !invitee.email.trim() && !invitee.role.trim();

export const isReadyInvitee = (invitee: InviteeDraft): boolean =>
  !!invitee.firstName.trim() && !!invitee.lastName.trim() && isValidEmail(invitee.email);

const emailKey = (invitee: InviteeDraft) => invitee.email.trim().toLowerCase();

/** The rows whose address another row already has; the API sends an address once, so only one may stay. */
export const repeatedEmails = (invitees: InviteeDraft[]): Set<number> => {
  const seen = new Set<string>();
  const repeats = new Set<number>();
  invitees.forEach((invitee) => {
    const key = emailKey(invitee);
    if (!key) return;
    if (seen.has(key)) repeats.add(invitee.rowId);
    seen.add(key);
  });
  return repeats;
};

/** The rows a send would carry: every row typed into, once each is complete and no two share an address. */
export const invitesToSend = (invitees: InviteeDraft[]): InviteeDraft[] | null => {
  const typed = invitees.filter((invitee) => !isBlankInvitee(invitee));
  const ready = typed.length > 0 && typed.every(isReadyInvitee) && repeatedEmails(typed).size === 0;
  return ready ? typed : null;
};

export const toScoringInvitee = (invitee: InviteeDraft): ScoringInvitee => ({
  first_name: invitee.firstName.trim(),
  last_name: invitee.lastName.trim(),
  email: invitee.email.trim(),
  role: invitee.role.trim() || null,
  scope: invitee.scope
});

/** Only an unanswered request can be handed to someone else, and only one blocks a new invite to the same address. */
export const isUnanswered = (row: ScoringRequestRow): boolean => row.status === 'sent' || row.status === 'viewed';

/** The invitees who hold an unanswered request with a live link, which a send would skip; a lapsed one is sent again. */
export const alreadyInvited = (invitees: InviteeDraft[], requests: ScoringRequestRow[]): InviteeDraft[] => {
  const open = new Set(
    requests.filter((row) => isUnanswered(row) && !row.link_lapsed).map((row) => row.email.toLowerCase())
  );
  return invitees.filter((invitee) => open.has(emailKey(invitee)));
};

/** The undelivered requests with no newer request to the same address, which a Retry sends again; rows are newest first. */
export const resendable = (requests: ScoringRequestRow[]): Set<string> => {
  const seen = new Set<string>();
  const ids = new Set<string>();
  requests.forEach((row) => {
    const email = row.email.toLowerCase();
    if (!seen.has(email) && row.status === 'not_delivered') ids.add(row.id);
    seen.add(email);
  });
  return ids;
};

/** The same person and scope again, as a new invite. */
export const inviteeOf = (row: ScoringRequestRow): ScoringInvitee => ({
  first_name: row.first_name,
  last_name: row.last_name,
  email: row.email,
  role: row.role,
  scope: row.all_categories ? [] : row.scope.map((category) => category.key)
});

export const fullName = (person: { first_name: string; last_name: string }): string =>
  `${person.first_name} ${person.last_name}`.trim();

export const initials = (person: { first_name: string; last_name: string }): string =>
  `${person.first_name.charAt(0)}${person.last_name.charAt(0)}`.toUpperCase();

export const scopeLabel = (row: ScoringRequestRow): string =>
  row.all_categories ? 'All categories' : row.scope.map((category) => category.label).join(', ');

const daysAgo = (iso: string, now: Date): string => {
  const days = differenceInCalendarDays(now, new Date(iso));
  if (days <= 0) return 'today';
  return days === 1 ? '1 day ago' : `${days} days ago`;
};

export const STATUS_TONES: Record<ScoringRequestStatus, 'neutral' | 'amber' | 'green' | 'red'> = {
  sent: 'neutral',
  viewed: 'amber',
  completed: 'green',
  reassigned: 'neutral',
  not_delivered: 'red'
};

export const statusLabel = (row: ScoringRequestRow, now: Date = new Date()): string => {
  switch (row.status) {
    case 'viewed':
      return row.first_viewed_at ? `Viewed ${daysAgo(row.first_viewed_at, now)}` : 'Viewed';
    case 'completed':
      return row.completed_at ? `Completed ${formatGeneratedAt(row.completed_at, 'MMM d, yyyy')}` : 'Completed';
    case 'reassigned':
      return row.reassigned_to ? `Reassigned → ${fullName(row.reassigned_to)}` : 'Reassigned';
    case 'not_delivered':
      return 'Email not delivered';
    default:
      return 'Sent';
  }
};
